import { useQuery } from "@tanstack/react-query";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";

import { TIMEFRAME_MS } from "@strat/format";
import type { Candle, IndicatorDef, IndicatorSeries, Trade } from "@strat/format";
import { CHART_KEY, ChartFilters, useChartView, useOpeningRange } from "@strat/ui";
import type { ChartProps } from "@strat/ui";

import { api } from "../api";
import { money, pct, timeAgo } from "../format";

/**
 * A live K-line chart for a bot: the archive's closed bars, the bar still
 * forming (refreshed every few seconds), the bot's own indicators, every
 * entry and exit it made, and the position it holds now.
 *
 * The chart itself is StrategyLab's — the same one the Workbench draws — so
 * a bot's chart and its backtest's chart look and read the same. It brings
 * Plotly, so it loads with the lab's chunk, only when someone opens a bot.
 */
const Chart = lazy(() =>
  import("../labPages").then((m) => ({ default: m.Chart as React.ComponentType<ChartProps> })),
);

const TF_MS: Record<string, number> = TIMEFRAME_MS;
/** Bars of context before the window a bot has been live for. */
const CONTEXT_BARS = 300;
/**
 * The most bars the chart asks for. The archive starts thinning a request to
 * every n-th bar past 3,000 — fine for a year of backtest, wrong for a live
 * chart whose point is the bars themselves — so stay under it and say how
 * many older trades that leaves off the chart.
 */
const MAX_BARS = 2_900;

type LiveTrade = Pick<Trade, "entryTime" | "entryPrice" | "exitTime" | "exitPrice"> & {
  direction?: string;
  pnlPct?: number;
  exitReason?: string;
};

export interface LiveChartProps {
  symbol: string;
  timeframe: string;
  /** Futures bots chart the perpetual's own bars. */
  marketType?: "spot" | "futures";
  indicators: IndicatorDef[];
  /** Closed trades. `pnlPct` is a fraction (0.012 = +1.2%), as everywhere else. */
  trades: LiveTrade[];
  position: null | { direction: "long" | "short"; entryTime: number; entryPrice: number };
  stopLossPct?: number;
  /** When the bot started: the chart covers at least that far back. */
  since?: number | null;
  running: boolean;
}

function useNow(ms: number): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [ms]);
  return now;
}

export function LiveChart({
  symbol,
  timeframe,
  indicators,
  trades,
  position,
  stopLossPct,
  since,
  running,
  marketType = "spot",
}: LiveChartProps) {
  const step = TF_MS[timeframe] ?? 3600e3;
  // Fixed per mount, so the query key (and the chart's zoom) stay put. Far
  // enough back to hold every trade — a chart whose markers run days past its
  // first candle squeezes the price into a sliver at one edge — but never so
  // far that the archive starts thinning the bars.
  const [from] = useState(() => {
    const firstTrade = trades.reduce((t, x) => Math.min(t, x.entryTime), Infinity);
    const want = Math.min(
      Date.now() - CONTEXT_BARS * step,
      (since ?? Date.now()) - 30 * step,
      Number.isFinite(firstTrade) ? firstTrade - 30 * step : Infinity,
    );
    return Math.max(want, Date.now() - MAX_BARS * step);
  });
  const opening = useOpeningRange(timeframe);
  const [initialRange] = useState(opening);
  const now = useNow(1000);

  const closed = useQuery({
    queryKey: ["hub-live-candles", symbol, timeframe, marketType, from],
    queryFn: () =>
      api<{ candles: Candle[] }>(
        `/candles?symbol=${symbol}&timeframe=${timeframe}&marketType=${marketType}&from=${from}`,
      ).then((r) => r.candles),
    refetchInterval: Math.min(step / 2, 30_000),
  });
  const lastClosed = closed.data?.at(-1)?.openTime ?? null;
  const series = useQuery({
    queryKey: [
      "hub-live-series",
      symbol,
      timeframe,
      marketType,
      from,
      lastClosed,
      indicators.map((d) => d.id).join(),
    ],
    queryFn: () =>
      api<{ series: IndicatorSeries[] }>("/indicators/series", {
        method: "POST",
        body: { symbol, timeframe, marketType, defs: indicators, from },
      }).then((r) => r.series),
    enabled: indicators.length > 0 && lastClosed !== null,
    placeholderData: (prev) => prev,
  });
  const live = useQuery({
    queryKey: ["hub-live-bar", symbol, timeframe, marketType],
    queryFn: () =>
      api<{ candle: Candle; closeTime: number; fetchedAt: number }>(
        `/data/live?symbol=${symbol}&timeframe=${timeframe}&marketType=${marketType}`,
      ),
    refetchInterval: 5_000,
    retry: false,
  });

  // Closed bars from the archive, then the one still forming.
  const candles = useMemo(() => {
    const bars = closed.data ?? [];
    const forming = live.data?.candle;
    if (!forming) return bars;
    const last = bars.at(-1);
    if (last && forming.openTime <= last.openTime) return bars;
    return [...bars, forming];
  }, [closed.data, live.data]);

  // Trades from before the first bar would stretch the time axis back to
  // them; leave them off and say how many.
  const firstBar = candles[0]?.openTime ?? -Infinity;
  const onChart = useMemo(() => trades.filter((t) => t.entryTime >= firstBar), [trades, firstBar]);
  const offChart = trades.length - onChart.length;
  const filters = useChartView(onChart);

  const stopPrice =
    position && stopLossPct
      ? position.entryPrice * (position.direction === "short" ? 1 + stopLossPct / 100 : 1 - stopLossPct / 100)
      : null;
  const forming = live.data?.candle;
  const barMove = forming && forming.open > 0 ? (forming.close / forming.open - 1) * 100 : null;
  const closesIn = live.data ? Math.max(0, live.data.closeTime - now) : null;
  const open =
    position && forming
      ? (forming.close / position.entryPrice - 1) * 100 * (position.direction === "short" ? -1 : 1)
      : null;
  const mmss = (ms: number) => {
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}:${String(s % 60).padStart(2, "0")}`;
  };

  return (
    <div className="live-chart">
      <div className="live-chart__status" aria-live="off">
        <span className={`live-dot${running && live.data ? " is-live" : ""}`} aria-hidden="true" />
        <strong>{running ? "Live" : "Stopped"}</strong>
        <span className="mono">
          {symbol} {timeframe}
        </span>
        {forming && (
          <>
            <span>
              last <strong className="mono">{money(forming.close, forming.close < 10 ? 4 : 2)}</strong>{" "}
              <span className={barMove == null ? "" : barMove >= 0 ? "up" : "down"}>
                ({pct(barMove, 2)} this bar)
              </span>
            </span>
            {closesIn !== null && <span className="muted">bar closes in {mmss(closesIn)}</span>}
          </>
        )}
        {position && (
          <span>
            {position.direction} from <span className="mono">{money(position.entryPrice, 2)}</span>
            {open !== null && <span className={open >= 0 ? "up" : "down"}> {pct(open, 2)}</span>}
            {stopPrice && <span className="muted"> · stop {money(stopPrice, 2)}</span>}
          </span>
        )}
        {live.data && <span className="muted small">updated {timeAgo(live.data.fetchedAt)}</span>}
        {live.isError && <span className="muted small">live price unavailable — showing closed bars</span>}
      </div>
      <ChartFilters
        state={filters}
        indicators={indicators}
        position={position}
        hasStop={stopPrice !== null}
      />
      {candles.length > 1 ? (
        <Suspense fallback={<p className="muted">Loading chart…</p>}>
          <Chart
            candles={candles}
            indicatorSeries={series.data ?? []}
            indicators={indicators}
            hiddenIds={filters.hiddenIds}
            trades={filters.shown as Trade[]}
            symbol={symbol}
            timeframe={timeframe}
            marketType={marketType}
            height={440}
            initialRange={initialRange}
            layers={filters.view}
            openPosition={position ? { ...position, stopPrice } : null}
          />
        </Suspense>
      ) : (
        <p className="muted">{closed.isLoading ? "Loading bars…" : "No bars yet for this market."}</p>
      )}
      <p className="muted small">
        {CHART_KEY} The last bar is still forming and updates every few seconds.
        {offChart > 0 &&
          ` ${offChart} older ${offChart === 1 ? "trade is" : "trades are"} before the first bar loaded and not drawn; the trade list below has ${offChart === 1 ? "it" : "them"}.`}
      </p>
    </div>
  );
}
